import React, { useState, useEffect, useMemo } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Slider } from '../components/ui/slider';
import { ArrowRight, Phone, Mail, Target, CheckCircle, Sparkles, Shield, Calendar, Bot, Globe, Megaphone, Users, Star, MapPin, Menu, X } from 'lucide-react';
import ChatBot from '../components/ChatBot';

const LOGO_URL = 'https://customer-assets-wrfwihn1.emergentagent.net/job_smile-automation-1/artifacts/ozqzxoju_Dentiflow%20Large%20Logo%20Plain.png';
const HERO_BG = 'https://images.unsplash.com/photo-1616391182219-e080b4d1043a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxkZW50YWwlMjBjbGluaWMlMjBpbnRlcmlvciUyMGRhcmslMjBtb2Rlcm58ZW58MHx8fHwxNzg1ODc3MDk5fDA&ixlib=rb-4.1.0&q=85';

// Animation configurations
const FADE_IN_UP = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8 }
};

const FADE_IN = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true }
};

const HEADER_ANIMATION = {
  initial: { y: -20, opacity: 0 },
  animate: { y: 0, opacity: 1 },
  transition: { duration: 0.6 }
};

// Additional animation variants for performance optimization
const STAGGER_ANIMATION = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.6 },
  viewport: { once: true }
};

const STAGGER_DELAY = (delay) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay },
  viewport: { once: true }
});

const CARD_HOVER = {
  whileHover: { y: -8 },
  transition: { duration: 0.3 }
};

const MOBILE_MENU_ANIMATION = {
  initial: { opacity: 0, x: '100%' },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: '100%' },
  transition: { duration: 0.3 }
};

// UI Constants
const SCROLL_THRESHOLD = 30;
const DEFAULT_MISSED_CALLS = 20;
const DEFAULT_AVG_APPOINTMENT_VALUE = 175;
const DEFAULT_CONVERSION_RATE = 60;

const HomePremium = () => {
  const [missedCalls, setMissedCalls] = useState([DEFAULT_MISSED_CALLS]);
  const [avgAppointmentValue, setAvgAppointmentValue] = useState(DEFAULT_AVG_APPOINTMENT_VALUE);
  const [conversionRate, setConversionRate] = useState(DEFAULT_CONVERSION_RATE);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showBookingPopup, setShowBookingPopup] = useState(true);
  const [bookingDecision, setBookingDecision] = useState(null); // null, 'yes', or 'no'
  const [formData, setFormData] = useState({
    name: '',
    practiceName: '',
    email: '',
    phone: '',
    message: ''
  });

  const missedCallsPerMonth = missedCalls[0];
  const monthlyLostRevenue = missedCallsPerMonth * avgAppointmentValue * (conversionRate / 100);
  const annualLostRevenue = monthlyLostRevenue * 12;

  // Load GoHighLevel booking calendar script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://link.msgsndr.com/js/form_embed.js';
    script.type = 'text/javascript';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup script on unmount
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []); // Script created fresh on mount, no external deps needed

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      const data = await response.json();
      alert(data.message || 'Thank you! We will contact you shortly.');
      setFormData({ name: '', practiceName: '', email: '', phone: '', message: '' });
      
    } catch (error) {
      alert('Sorry, there was an error submitting your form. Please try again or call us directly.');
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white font-sans">
      {/* Glassmorphic Header */}
      <motion.header 
        {...HEADER_ANIMATION}
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-4 md:py-5 flex justify-between items-center">
          <img src={LOGO_URL} alt="DentiFlow" className="h-8 md:h-10 w-auto" />
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8 text-sm">
            <button 
              onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
              className="text-white hover:text-[#4FC3F7] transition-colors font-medium"
            >
              Services
            </button>
            <button 
              onClick={() => document.getElementById('calculator').scrollIntoView({ behavior: 'smooth' })}
              className="text-white hover:text-[#4FC3F7] transition-colors font-medium"
            >
              Calculator
            </button>
            <button 
              onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}
              className="text-white hover:text-[#4FC3F7] transition-colors font-medium"
            >
              How It Works
            </button>
            <button 
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              className="text-white hover:text-[#4FC3F7] transition-colors font-medium"
            >
              Contact
            </button>
          </nav>

          {/* Desktop CTA and Contact Info */}
          <div className="hidden md:flex items-center gap-6">
            <div className="hidden lg:flex items-center gap-4 text-xs text-gray-400">
              <a href="mailto:info@dentiflow.co.uk" className="hover:text-[#4FC3F7] transition-colors">
                info@dentiflow.co.uk
              </a>
              <span className="text-gray-600">|</span>
              <a href="tel:02045385175" className="hover:text-[#4FC3F7] transition-colors">
                020 4538 5175
              </a>
            </div>
            <Button 
              data-testid="header-cta-button"
              className="bg-[#4FC3F7] text-black hover:bg-white hover:text-black font-semibold transition-colors rounded-md px-6"
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            >
              Book Consultation
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-white hover:text-[#4FC3F7] transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="md:hidden border-t border-white/10 bg-black/95 backdrop-blur-xl"
          >
            <nav className="flex flex-col px-4 py-4 space-y-3">
              <button 
                onClick={() => {
                  document.getElementById('services').scrollIntoView({ behavior: 'smooth' });
                  setMobileMenuOpen(false);
                }}
                className="text-left py-3 px-4 text-white hover:text-[#4FC3F7] hover:bg-white/5 rounded-md transition-all font-medium"
              >
                Services
              </button>
              <button 
                onClick={() => {
                  document.getElementById('calculator').scrollIntoView({ behavior: 'smooth' });
                  setMobileMenuOpen(false);
                }}
                className="text-left py-3 px-4 text-white hover:text-[#4FC3F7] hover:bg-white/5 rounded-md transition-all font-medium"
              >
                Calculator
              </button>
              <button 
                onClick={() => {
                  document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' });
                  setMobileMenuOpen(false);
                }}
                className="text-left py-3 px-4 text-white hover:text-[#4FC3F7] hover:bg-white/5 rounded-md transition-all font-medium"
              >
                How It Works
              </button>
              <button 
                onClick={() => {
                  document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
                  setMobileMenuOpen(false);
                }}
                className="text-left py-3 px-4 text-white hover:text-[#4FC3F7] hover:bg-white/5 rounded-md transition-all font-medium"
              >
                Contact
              </button>
              <Button 
                className="w-full bg-[#4FC3F7] text-black hover:bg-white hover:text-black font-semibold transition-colors rounded-md py-6 mt-2"
                onClick={() => {
                  document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
                  setMobileMenuOpen(false);
                }}
              >
                Book Free Consultation
              </Button>
            </nav>
          </motion.div>
        )}
      </motion.header>

      {/* Hero Section - Asymmetrical High-Impact */}
      <section className="relative min-h-[85vh] sm:min-h-screen flex items-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src={HERO_BG} 
            alt="Modern dental clinic" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#1F2937]/70"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto my-12 sm:my-16 md:my-20 px-8 sm:px-10 md:px-16 py-12 sm:py-14 md:py-16 bg-[#4FC3F7] shadow-2xl">
          <motion.div
            {...FADE_IN_UP}
            className="max-w-4xl"
          >
            <h1 className="font-heading font-bold text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[1.1] mb-6 sm:mb-8 text-black">
              Spend Less Time,<br />
              Book More Patients
            </h1>
            <p className="text-sm sm:text-base md:text-lg leading-relaxed text-black mb-8 sm:mb-10 md:mb-12 max-w-2xl">
              Transform your practice with AI-powered solutions. Never miss a call, automate patient communications, and watch your bookings grow—all while you focus on delivering exceptional care.
            </p>
            
            <div className="flex flex-col gap-3 sm:gap-4 mb-12 sm:mb-14 md:mb-16">
              <Button 
                data-testid="hero-cta-button"
                size="lg"
                className="w-full sm:w-auto bg-[#1F2937] hover:bg-[#374151] text-white font-semibold transition-colors rounded-md px-8 sm:px-10 py-6 sm:py-7 text-base sm:text-lg min-h-[56px] justify-center"
                onClick={() => document.getElementById('calculator').scrollIntoView({ behavior: 'smooth' })}
              >
                Try Our Lost Revenue Calculator
              </Button>
              <Button 
                size="lg"
                className="w-full sm:w-auto bg-[#1F2937] hover:bg-[#374151] text-white font-semibold transition-colors rounded-md px-8 sm:px-10 py-6 sm:py-7 text-base sm:text-lg min-h-[56px]"
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              >
                Book a Free Consultation
              </Button>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-3 sm:gap-4 md:gap-6 text-xs sm:text-sm">
              <div className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-[#1F2937] rounded-md border border-[#374151]">
                <Sparkles className="w-4 h-4 flex-shrink-0 text-[#4FC3F7]" />
                <span className="text-white font-medium">Built by a Harley Street Professional</span>
              </div>
              <div className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-[#1F2937] rounded-md border border-[#374151]">
                <Shield className="w-4 h-4 flex-shrink-0 text-[#4FC3F7]" />
                <span className="text-white font-medium">GDPR Compliant</span>
              </div>
              <div className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-[#1F2937] rounded-md border border-[#374151]">
                <Calendar className="w-4 h-4 flex-shrink-0 text-[#4FC3F7]" />
                <span className="text-white font-medium">24/7 Support</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats - High Contrast */}
      <section className="py-10 sm:py-16 md:py-20 bg-[#0F1115] border-y border-[#27272A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              { id: 'stat-calls', value: '98%', label: 'Call Answer Rate' },
              { id: 'stat-available', value: '24/7', label: 'Always Available' },
              { id: 'stat-savings', value: '£50k+', label: 'Average Annual Savings' }
            ].map((stat, i) => (
              <motion.div
                key={stat.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-5xl md:text-6xl font-heading font-medium text-[#4FC3F7] mb-3">{stat.value}</div>
                <div className="text-base text-gray-400">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services - Flip Cards */}
      <section id="services" className="py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl tracking-tight font-medium text-white mb-4">
              Core Services
            </h2>
            <p className="text-base md:text-lg text-gray-400 max-w-2xl">
              A focused service, delivered end to end. Hover over a card to view more details.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { 
                id: 'service-1',
                title: 'AI Appointment Setter', 
                icon: Bot,
                description: 'Automated appointment booking and rescheduling. Our AI handles incoming calls 24/7, books appointments directly into your calendar, and follows up with confirmations.'
              },
              { 
                id: 'service-2',
                title: 'High-Converting Websites', 
                icon: Globe,
                description: 'Beautiful, fast-loading websites designed to convert visitors into patients. Mobile-optimized with clear calls-to-action and seamless booking integration.'
              },
              { 
                id: 'service-3',
                title: 'AI Powered Marketing', 
                icon: Megaphone,
                description: 'Intelligent ad campaigns across Google, Facebook, and Instagram. AI optimizes your budget in real-time, targeting the right patients at the right time.'
              },
              { 
                id: 'service-4',
                title: 'Lead Re-activation Campaigns', 
                icon: Users,
                description: 'Win back inactive patients with personalized email and SMS campaigns. Automated follow-ups for missed appointments and lapsed patients.'
              },
              { 
                id: 'service-5',
                title: 'Google Review Optimisation', 
                icon: Star,
                description: 'Automated review requests sent at the perfect moment. Build your online reputation with a steady stream of 5-star reviews on Google.'
              },
              { 
                id: 'service-6',
                title: 'Local SEO and Visibility', 
                icon: MapPin,
                description: 'Dominate local search results. Google My Business optimization, local citations, and content strategy that puts your practice at the top.'
              },
            ].map((service, i) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="group perspective-1000"
                style={{ perspective: '1000px' }}
              >
                <div className="relative h-[280px] transition-transform duration-500 preserve-3d group-hover:rotate-y-180"
                     style={{ transformStyle: 'preserve-3d' }}>
                  {/* Front of card */}
                  <div className="absolute inset-0 backface-hidden bg-[#0F1115] border-2 border-[#27272A] p-8 flex flex-col items-center justify-center text-center"
                       style={{ backfaceVisibility: 'hidden' }}>
                    <service.icon className="w-16 h-16 text-[#4FC3F7] mb-4" />
                    <h3 className="font-heading text-xl font-medium text-white">
                      {service.title}
                    </h3>
                  </div>
                  
                  {/* Back of card */}
                  <div className="absolute inset-0 backface-hidden bg-[#4FC3F7] p-8 flex flex-col items-center justify-between rotate-y-180"
                       style={{ 
                         backfaceVisibility: 'hidden',
                         transform: 'rotateY(180deg)'
                       }}>
                    <p className="text-black leading-relaxed text-center flex-grow flex items-center">
                      {service.description}
                    </p>
                    <button
                      onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                      className="text-black text-sm font-semibold hover:underline transition-all mt-4"
                    >
                      Enquire Now →
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Calculator - High Density Tech Grid */}
      <section id="calculator" className="py-12 sm:py-16 md:py-24 bg-[#050505]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mb-8 sm:mb-10 md:mb-12"
          >
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl tracking-tight font-medium text-white mb-3 sm:mb-4 text-center">
              Calculate Your Lost Revenue
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-400 text-center px-4">
              See how much missed leads are costing your practice
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="bg-[#0F1115] border border-[#27272A] p-6 sm:p-8 md:p-12"
          >
            <Label className="text-base sm:text-lg font-medium text-white mb-4 sm:mb-6 block">
              How many leads does your practice miss a month?
            </Label>
            <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 md:gap-8 mb-8 sm:mb-10 md:mb-12">
              <Slider
                data-testid="revenue-calculator-slider"
                value={missedCalls}
                onValueChange={setMissedCalls}
                max={100}
                min={0}
                step={1}
                className="flex-grow w-full sm:w-auto"
              />
              <div className="text-5xl sm:text-6xl font-heading font-medium text-[#4FC3F7] min-w-[100px] sm:min-w-[120px] text-center sm:text-right tabular-nums">
                {missedCalls[0]}
              </div>
            </div>

            {/* Custom Input Fields */}
            <div className="grid gap-4 sm:gap-6 mb-6 sm:mb-8">
              <div>
                <Label className="text-sm font-medium text-white mb-2 block">
                  Average Appointment Value (£)
                </Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={avgAppointmentValue}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    setAvgAppointmentValue(val ? parseInt(val, 10) : 0);
                  }}
                  className="bg-[#1F2937] border-[#374151] text-white text-lg py-6 px-4 min-h-[56px]"
                  placeholder="175"
                />
              </div>
              
              <div>
                <Label className="text-sm font-medium text-white mb-2 block">
                  Conversion Rate (%)
                </Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={conversionRate}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    const numVal = val ? parseInt(val, 10) : 0;
                    setConversionRate(Math.min(100, Math.max(0, numVal)));
                  }}
                  className="bg-[#1F2937] border-[#374151] text-white text-lg py-6 px-4 min-h-[56px]"
                  placeholder="60"
                />
              </div>
            </div>

            <div className="grid gap-4 sm:gap-6 mb-8 sm:mb-10">
              <div className="p-6 sm:p-8 bg-[#161A22] border border-[#27272A]">
                <div className="text-xs sm:text-sm text-gray-400 mb-2">Monthly Lost Revenue</div>
                <div className="text-3xl sm:text-4xl font-heading font-medium text-white tabular-nums">
                  £{monthlyLostRevenue.toLocaleString()}
                </div>
              </div>
              <div className="p-6 sm:p-8 bg-[#4FC3F7]">
                <div className="text-xs sm:text-sm text-black/70 mb-2">Annual Lost Revenue</div>
                <div className="text-3xl sm:text-4xl font-heading font-medium text-black tabular-nums">
                  £{annualLostRevenue.toLocaleString()}
                </div>
              </div>
            </div>

            <div className="text-xs sm:text-sm text-gray-500 mb-6 sm:mb-8 p-3 sm:p-4 bg-[#161A22] border border-[#27272A]">
              Based on £{avgAppointmentValue} average appointment value and {conversionRate}% conversion rate
            </div>

            <Button 
              data-testid="calculator-cta-button"
              size="lg"
              className="w-full bg-[#4FC3F7] hover:bg-white text-black font-semibold transition-colors rounded-md py-6 sm:py-7 text-base sm:text-lg min-h-[56px]"
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            >
              <span className="hidden sm:inline">Stop Losing Revenue - Book Free Consultation</span>
              <span className="sm:hidden">Book Free Consultation</span>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* How It Works - Staggered Timeline */}
      <section id="how-it-works" className="py-12 sm:py-20 md:py-28 bg-[#0F1115]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl tracking-tight font-medium text-white mb-4 text-center">
              How It Works
            </h2>
          </motion.div>

          <div className="space-y-12">
            {[
              { 
                id: 'step-01', 
                num: '01', 
                title: 'Free Consultation', 
                desc: 'Book a 30 minute call to help map where your clinic grows. Where patients come from, how your brand is perceived online, and where the biggest acquisition opportunities are being missed.',
                align: 'left'
              },
              { 
                id: 'step-02', 
                num: '02', 
                title: 'We Build Your Growth System', 
                desc: 'AI marketing campaigns, SEO strategy, website design, review automation, and social content, all tailored to your clinic.',
                align: 'right'
              },
              { 
                id: 'step-03', 
                num: '03', 
                title: 'We Build And Connect Everything', 
                desc: 'Your website, ads, SEO, reviews, and social channels all work together to drive a steady flow of new patient enquiries.',
                align: 'left'
              },
              { 
                id: 'step-04', 
                num: '04', 
                title: 'Go Live And Optimise', 
                desc: 'Track leads, conversions, and ROI, then refine campaigns continuously with real performance data.',
                align: 'right'
              },
            ].map((step, i) => (
              <motion.div
                key={step.id}
                initial={{ opacity: 0, x: step.align === 'left' ? -40 : 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                whileHover={{ 
                  scale: 1.03, 
                  y: -8,
                  transition: { duration: 0.3, ease: "easeOut" }
                }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                viewport={{ once: true }}
                className={`flex gap-8 items-start p-8 bg-[#050505] border border-[#27272A] hover:border-[#4FC3F7] hover:shadow-2xl hover:shadow-[#4FC3F7]/20 transition-all duration-300 cursor-pointer ${
                  step.align === 'left' ? 'md:mr-24' : 'md:ml-24'
                }`}
              >
                <div className="flex-shrink-0 w-16 h-16 bg-[#4FC3F7] flex items-center justify-center">
                  <span className="text-2xl font-heading font-bold text-black">{step.num}</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-heading text-xl font-medium text-white mb-3">{step.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact" className="py-12 sm:py-16 md:py-24 bg-[#050505]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mb-8 sm:mb-10 md:mb-12"
          >
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl tracking-tight font-medium text-white mb-3 sm:mb-4 text-center">
              Ready to Transform Your Practice?
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-400 text-center px-4">
              Book a 30 minute free consultation using our calendar below
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="bg-[#0F1115] border border-[#27272A] p-4 sm:p-6 md:p-8 rounded-md relative min-h-[900px]"
          >
            {/* Booking Confirmation Popup */}
            {showBookingPopup && bookingDecision === null && (
              <div className="absolute inset-0 bg-black/80 backdrop-blur-sm rounded-md flex items-center justify-center z-10 p-4">
                <motion.div 
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                  className="bg-[#1F2937] border-2 border-[#4FC3F7] rounded-lg p-6 sm:p-8 max-w-md w-full"
                >
                  <h3 className="text-xl sm:text-2xl font-semibold text-white mb-4 text-center">
                    Would you like to make a booking?
                  </h3>
                  <div className="flex flex-col sm:flex-row gap-4 mt-6">
                    <Button
                      onClick={() => {
                        setBookingDecision('yes');
                        setShowBookingPopup(false);
                      }}
                      className="flex-1 bg-[#4FC3F7] hover:bg-[#3FB3E7] text-black font-semibold py-6 text-lg rounded-md transition-colors"
                    >
                      Yes
                    </Button>
                    <Button
                      onClick={() => setBookingDecision('no')}
                      className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-semibold py-6 text-lg rounded-md transition-colors"
                    >
                      No
                    </Button>
                  </div>
                </motion.div>
              </div>
            )}

            {/* "No" Response Message */}
            {bookingDecision === 'no' && (
              <div className="absolute inset-0 bg-black/80 backdrop-blur-sm rounded-md flex items-center justify-center z-10 p-4">
                <motion.div 
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                  className="bg-[#1F2937] border-2 border-[#4FC3F7] rounded-lg p-6 sm:p-8 max-w-md w-full"
                >
                  <h3 className="text-xl sm:text-2xl font-semibold text-white mb-4 text-center">
                    Need More Information?
                  </h3>
                  <p className="text-gray-300 text-center mb-6 leading-relaxed">
                    If you require any further assistance, please contact{' '}
                    <a 
                      href="mailto:info@dentiflow.co.uk" 
                      className="text-[#4FC3F7] hover:underline font-medium"
                    >
                      info@dentiflow.co.uk
                    </a>
                    {' '}for more information regarding bookings.
                  </p>
                  <Button
                    onClick={() => {
                      setBookingDecision(null);
                      setShowBookingPopup(true);
                    }}
                    className="w-full bg-[#4FC3F7] hover:bg-[#3FB3E7] text-black font-semibold py-4 rounded-md transition-colors"
                  >
                    Back to Booking Options
                  </Button>
                </motion.div>
              </div>
            )}

            {/* GoHighLevel Booking Calendar - Only shown after "Yes" */}
            {bookingDecision === 'yes' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="w-full h-full overflow-hidden rounded-md min-h-[850px]"
              >
                <iframe 
                  src="https://api.leadconnectorhq.com/widget/booking/OHJMScEaRdNZb56UXKs5" 
                  allow="payment" 
                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                  referrerPolicy="strict-origin-when-cross-origin"
                  style={{ width: '100%', height: '850px', border: 'none' }} 
                  scrolling="no" 
                  id="OHJMScEaRdNZb56UXKs5_1786492935127"
                  title="Book a Free Consultation"
                  className="rounded-md"
                />
              </motion.div>
            )}

            {/* Placeholder when decision not made */}
            {bookingDecision === null && !showBookingPopup && (
              <div className="h-[600px] flex items-center justify-center">
                <p className="text-gray-500">Loading booking options...</p>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black py-8 sm:py-10 md:py-12 border-t-2 border-[#4FC3F7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 text-center">
          <img src={LOGO_URL} alt="DentiFlow" className="h-12 sm:h-14 w-auto mx-auto mb-4 sm:mb-6 opacity-90" />
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 mb-4 text-gray-400 text-sm">
            <a href="mailto:info@dentiflow.co.uk" className="hover:text-[#4FC3F7] transition-colors">
              info@dentiflow.co.uk
            </a>
            <span className="hidden sm:inline text-gray-600">|</span>
            <a href="tel:02045385175" className="hover:text-[#4FC3F7] transition-colors">
              020 4538 5175
            </a>
          </div>
          <p className="text-gray-500 text-xs sm:text-sm">© {new Date().getFullYear()} DentiFlow. All rights reserved.</p>
        </div>
      </footer>

      <ChatBot />
    </div>
  );
};

export default HomePremium;
